/**
 * Actions Field Type
 *
 * Provides custom column renderer for action buttons (edit, delete, copy, etc.).
 * This type is read-only only (actions are not editable inline).
 */

import type { ColumnDef } from '@tanstack/react-table';
import type { ActionsConfig, ColumnRenderer, RenderOptions, CustomColumn } from '../../types';

// ============================================================================
// Helper - Import createStandardActionsColumn lazily
// ============================================================================

// Store reference to createStandardActionsColumn to avoid circular dependencies
let createStandardActionsColumnFn:
  | (<T extends { id: number | string }>(config: ActionsConfig<T>) => Partial<ColumnDef<T>>)
  | null = null;

/**
 * Set the createStandardActionsColumn function (called from index.ts to avoid circular deps)
 */
export const setCreateStandardActionsColumn = (
  fn: <T extends { id: number | string }>(config: ActionsConfig<T>) => Partial<ColumnDef<T>>
): void => {
  createStandardActionsColumnFn = fn;
};

// ============================================================================
// Read-Only Column Renderer
// ============================================================================

/**
 * Actions read-only renderer - renders action buttons for row operations
 * Uses actions config with handlers for edit, delete, copy, read
 */
export const actionsReadOnlyRenderer = <T extends { id: number | string }>(
  col: CustomColumn<T>,
  _options: RenderOptions<T>
): Partial<ColumnDef<T>> => {
  // If actions config exists, use standard actions column
  const colWithActions = col as CustomColumn<T> & { actions?: ActionsConfig<T> };
  if (
    colWithActions.actions &&
    'handlers' in colWithActions.actions &&
    createStandardActionsColumnFn
  ) {
    return createStandardActionsColumnFn<T>(colWithActions.actions);
  }
  // Fallback - return empty object
  return {};
};

// ============================================================================
// Typed Renderer Exports (for registry)
// ============================================================================

/** Read-only renderer with proper typing */
export const actionsReadOnly: ColumnRenderer = actionsReadOnlyRenderer;

/** Editable renderer - null because actions columns are not editable */
export const actionsEditable: ColumnRenderer | null = null;
